/**
 * Pure helpers for translating an "order edit" diff into prompts and
 * `change_events` insert payloads. Keeps OrderEditDialog and the Orders page
 * free of matrix knowledge — they only deal with `ChangeReasonPrompt` and
 * `ChangeEventInput` shapes.
 */
import type { ChangeReasonPrompt, ChangeReasonResult } from "@/components/orders/ChangeReasonDialog";
import type { ChangeEventInput, ChangeEventReason } from "@/lib/change-events";

export interface PivotSnapshot {
  trip_type?: string | null;
  trailer_id?: string | null;
  assigned_tractor_id?: string | null;
}

export interface PivotContext {
  orderId: string;
  tenantId: string;
  eapiTripId?: string | null;
}

const norm = (v: unknown) => (v == null ? "" : String(v).trim());

/**
 * Optional resolver so callers can turn opaque ids (e.g. tractor uuids) into
 * human-readable labels for the "original → pivoted" line in the dialog.
 */
export type PivotLabelResolver = (
  field: keyof PivotSnapshot,
  value: string | null,
) => string | null;

/** Identify which equipment / trip_type fields changed between two snapshots. */
export function detectPivotPrompts(
  original: PivotSnapshot,
  next: PivotSnapshot,
  resolveLabel?: PivotLabelResolver,
): ChangeReasonPrompt[] {
  const prompts: ChangeReasonPrompt[] = [];

  const label = (field: keyof PivotSnapshot, raw: unknown) => {
    const value = norm(raw) || null;
    return (resolveLabel?.(field, value) ?? value) || null;
  };

  const changed = (a: unknown, b: unknown) => {
    const na = norm(a);
    const nb = norm(b);
    // Only prompt when shifting between two non-empty values.
    // Empty → value (initial fill) and value → empty (clear) don't require a reason.
    return na !== nb && na !== "" && nb !== "";
  };

  if (changed(original.trip_type, next.trip_type)) {
    const from = label("trip_type", original.trip_type);
    const to = label("trip_type", next.trip_type);
    prompts.push({
      key: "trip_type",
      eventType: "trip_type_updated",
      detail: `${from ?? "—"} → ${to ?? "—"}`,
      fromLabel: from,
      toLabel: to,
    });
  }
  if (changed(original.trailer_id, next.trailer_id)) {
    const from = label("trailer_id", original.trailer_id);
    const to = label("trailer_id", next.trailer_id);
    prompts.push({
      key: "trailer_id",
      eventType: "trip_equipment_trailer",
      detail: `${from ?? "—"} → ${to ?? "—"}`,
      fromLabel: from,
      toLabel: to,
    });
  }
  if (changed(original.assigned_tractor_id, next.assigned_tractor_id)) {
    // Tractors are stored as uuids — only surface a from → to line when the
    // caller supplied a resolver that can map them to unit names.
    const from = resolveLabel
      ? resolveLabel("assigned_tractor_id", norm(original.assigned_tractor_id) || null)
      : null;
    const to = resolveLabel
      ? resolveLabel("assigned_tractor_id", norm(next.assigned_tractor_id) || null)
      : null;
    prompts.push({
      key: "assigned_tractor_id",
      eventType: "trip_equipment_tractor",
      detail: from || to ? `${from ?? "—"} → ${to ?? "—"}` : undefined,
      fromLabel: from,
      toLabel: to,
    });
  }
  return prompts;
}

/** Convert a dialog result-set back into `change_events` insert rows. */
export function buildPivotInputs(
  original: PivotSnapshot,
  next: PivotSnapshot,
  results: ChangeReasonResult[],
  ctx: PivotContext,
): ChangeEventInput[] {
  const fieldByKey: Record<string, keyof PivotSnapshot> = {
    trip_type: "trip_type",
    trailer_id: "trailer_id",
    assigned_tractor_id: "assigned_tractor_id",
  };
  return results.map((r) => {
    const field = fieldByKey[r.key];
    return {
      orderId: ctx.orderId,
      tenantId: ctx.tenantId,
      eapiTripId: ctx.eapiTripId ?? null,
      eventType: r.eventType,
      reason: r.reason,
      otherText: r.otherText,
      previousValue: field ? { [field]: norm(original[field]) || null } : null,
      newValue: field ? { [field]: norm(next[field]) || null } : null,
    };
  });
}

export interface CancelContext extends PivotContext {
  previousStatus: string | null | undefined;
}

/**
 * Build the canonical `trip_cancelled` change_event row for the OrderEditDialog
 * cancel path. Returns `null` when there's nothing to record (no reason
 * staged, missing tenant, or the order wasn't actually transitioning into
 * canceled). Centralizing the gate here keeps the dialog from inserting
 * phantom rows when the user backs out of Save (see AOS3-AURY-00398).
 */
export function buildCancelChangeEventInput(args: {
  ctx: CancelContext;
  staged: { reason: ChangeEventReason; otherText: string | null } | null;
  nextStatus: string | null | undefined;
}): ChangeEventInput | null {
  const { ctx, staged, nextStatus } = args;
  if (!staged) return null;
  if (!ctx.tenantId) return null;
  if (nextStatus !== "canceled") return null;
  if (ctx.previousStatus === "canceled") return null;
  return {
    orderId: ctx.orderId,
    tenantId: ctx.tenantId,
    eapiTripId: ctx.eapiTripId ?? null,
    eventType: "trip_cancelled",
    reason: staged.reason,
    otherText: staged.otherText,
    previousValue: { status: ctx.previousStatus ?? null },
    newValue: { status: "canceled" },
  };
}
